import { useMediaQuery } from "@mui/material";
import { bbox, FeatureCollection } from "@turf/turf";
import {
  ArableFieldGeoJSONLayers,
  FieldGeoJSONLayers,
} from "features/mapLayers";
import { useEffect, useRef, useState } from "react";
import MapGL, {
  MapRef,
  ViewportProps,
  WebMercatorViewport,
} from "react-map-gl";
import styled, { useTheme } from "styled-components";

interface FieldMapPreviewProps {
  geojson: FeatureCollection;
  isArable?: boolean;
  fieldIds?: string[];
}

const initialViewport: ViewportProps = {
  latitude: 52.794201,
  longitude: -1.1945036,
  zoom: 12,
  bearing: 0,
  pitch: 0,
  width: 280,
  height: 120,
};

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;

  .mapboxgl-ctrl-logo {
    display: none !important;
  }
  .mapboxgl-ctrl-attrib {
    display: none !important;
  }
`;

export default function FieldMapPreview({
  geojson,
  isArable,
  fieldIds,
}: FieldMapPreviewProps) {
  const mapRef = useRef<MapRef | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewport, setViewport] = useState<ViewportProps>(initialViewport);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getViewportForBounds = (
    geojson: FeatureCollection,
    width: number,
    height: number
  ): ViewportProps => {
    if (!width || !height || geojson.features.length === 0) {
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    // Validate viewport dimensions
    if (width <= 0 || height <= 0 || !isFinite(width) || !isFinite(height)) {
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    const [minLng, minLat, maxLng, maxLat] = bbox(geojson);

    // Validate bounding box coordinates
    if (
      !isFinite(minLng) ||
      !isFinite(minLat) ||
      !isFinite(maxLng) ||
      !isFinite(maxLat) ||
      minLng >= maxLng ||
      minLat >= maxLat
    ) {
      // Invalid bounding box - return initial viewport with current dimensions
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    // Validate padding doesn't exceed viewport dimensions
    const padding = 20;
    if (padding * 2 >= width || padding * 2 >= height) {
      // Padding too large - return initial viewport with current dimensions
      return {
        ...initialViewport,
        width,
        height,
      };
    }

    try {
      const viewport = new WebMercatorViewport({
        ...initialViewport,
        width,
        height,
      });

      const { longitude, latitude, zoom } = viewport.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: 20,
        }
      );

      // Validate the returned values
      if (!isFinite(longitude) || !isFinite(latitude) || !isFinite(zoom)) {
        return {
          ...initialViewport,
          width,
          height,
        };
      }

      return {
        ...initialViewport,
        longitude,
        latitude,
        zoom: zoom - 0.5, // Zoom out slightly to give some padding
        width,
        height,
      };
    } catch (error) {
      // If fitBounds throws an error (e.g., assertion failure), return initial viewport
      console.warn("Error fitting bounds to viewport:", error);
      return {
        ...initialViewport,
        width,
        height,
      };
    }
  };

  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      const newViewport = getViewportForBounds(geojson, width, height);
      setViewport(newViewport);
    }
  }, [geojson]);

  const handleTransformRequest = (url: string) => {
    if (url?.includes(`${process.env.REACT_APP_API_ENDPOINT}`)) {
      return {
        url: url,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt")}`,
          "Content-Type": "application/json",
        },
      };
    }
    return { url };
  };

  const noop = () => undefined;

  const satelliteMap =
    "mapbox://styles/robofarmio/ckbaq6k7p0rmc1irr9el63loo?optimize=true";

  return (
    <MapContainer ref={containerRef}>
      <MapGL
        ref={mapRef}
        {...viewport}
        mapStyle={satelliteMap}
        mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
        dragPan={false}
        dragRotate={false}
        scrollZoom={false}
        doubleClickZoom={false}
        touchZoom={false}
        touchRotate={false}
        keyboard={false}
        maxPitch={0}
        minPitch={0}
        transformRequest={handleTransformRequest}
        attributionControl={false}
      >
        <FieldGeoJSONLayers
          fieldIds={fieldIds}
          fillLayers={true}
          setTooltipOverMarker={noop}
          mapRef={mapRef}
        />
        <ArableFieldGeoJSONLayers
          fieldIds={fieldIds}
          fillLayers={true}
          setTooltipOverMarker={noop}
          mapRef={mapRef}
        />
      </MapGL>
    </MapContainer>
  );
}
